import { log, logV } from "../../services/debug-logger";
import {
  DEFAULT_VIRTUALIZED_MAX_HEIGHT,
  EMPTY_COLUMN_WIDTH_ROWS,
  GRID_REGION_LABEL,
  GRID_SCROLLBAR_CLASS_NAME,
  GRID_SCROLLER_CLASS_NAME,
  GRID_TABLE_CLASS_NAME,
} from "../constants/excel-like-grid.constants";
import type { ExcelLikeGridProps } from "../models/excel-grid-component-props.models";
import {
  ExcelGridColumn,
  ExcelGridEditCellState,
  ExcelGridFilterState,
  ExcelGridRow,
  ExcelGridSelection,
  ExcelGridSortState,
} from "../models/excel-grid.models";
import {
  getColumnWidths,
  getVisibleRows,
  hasActiveFilters,
  moveSelection,
  toCellText,
} from "../utils/excel-grid.utils";
import {
  DEFAULT_VIRTUALIZED_ROW_HEIGHT,
  getActiveElementLabel,
  getEstimatedVirtualizedGridContentHeight,
  getKeyboardMovement,
  getVirtualizedGridHeightClassName,
  omitInlineStyleProp,
} from "../utils/excel-like-grid-ui.utils";
import { ExcelGridBody } from "./ExcelGridBody";
import { ExcelGridColGroup } from "./ExcelGridColGroup";
import { ExcelGridFilteredEmptyBody } from "./ExcelGridFilteredEmptyBody";
import { ExcelGridHeader } from "./ExcelGridHeader";
import { ExcelGridHeaderRow } from "./ExcelGridHeaderRow";
import { ExcelGridRowCells } from "./ExcelGridRowCells";
import { ExcelGridVirtualTableHead } from "./ExcelGridVirtualTableHead";
import "./ExcelLikeGrid.css";
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableVirtuoso, type TableComponents, type TableVirtuosoHandle } from "react-virtuoso";
import { twMerge } from "tailwind-merge";

/**
 * Spreadsheet-like text grid with cell selection, keyboard navigation, and enter-to-edit behavior.
 */
export const ExcelLikeGrid = ({
  rows,
  columns,
  dataTest,
  emptyLabel = "No rows.",
  onCellCommit,
  onSelectedRowChange,
  isSavingCell,
  showHeadersWhenEmpty = false,
  rowHeight,
  className,
  virtualized = false,
  virtualizedMaxHeight = DEFAULT_VIRTUALIZED_MAX_HEIGHT,
  useContentColumnWidths = true,
  cellEditorMode = "multiline",
  editingDisabled = false,
  useTabForCellNavigation = true,
  selectMenuPortalTarget,
  filterState,
  onFilterStateChange,
}: ExcelLikeGridProps) => {
  const [selection, setSelection] = useState<ExcelGridSelection>({ rowIndex: 0, columnIndex: 0 });
  const [editing, setEditing] = useState<ExcelGridEditCellState | null>(null);
  const [sortState, setSortState] = useState<ExcelGridSortState | null>(null);
  const [internalFilters, setInternalFilters] = useState<ExcelGridFilterState>({});
  const filters = filterState ?? internalFilters;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const virtualGridRef = useRef<TableVirtuosoHandle | null>(null);
  const virtualScrollerRef = useRef<HTMLElement | null>(null);
  const selectedCellRef = useRef<HTMLTableCellElement | null>(null);
  const rowsForColumnWidths = useContentColumnWidths ? rows : EMPTY_COLUMN_WIDTH_ROWS;
  const columnWidths = useMemo(() => getColumnWidths(columns, rowsForColumnWidths), [columns, rowsForColumnWidths]);
  const tableWidth = useMemo(
    () => Object.values(columnWidths).reduce((total, width) => total + width, 0),
    [columnWidths],
  );
  const visibleRows = useMemo(
    () => getVisibleRows(rows, columns, filters, sortState),
    [columns, filters, rows, sortState],
  );
  const hasFilters = useMemo(() => hasActiveFilters(filters), [filters]);
  const virtualGridEstimatedHeight = useMemo(
    () => getEstimatedVirtualizedGridContentHeight(visibleRows.length, rowHeight),
    [rowHeight, visibleRows.length],
  );
  const shouldUseVirtualizedGrid =
    virtualized && visibleRows.length > 0 && virtualGridEstimatedHeight > virtualizedMaxHeight;

  logV("ExcelLikeGrid - BUILD", { rows: rows.length, visibleRows: visibleRows.length, editing: Boolean(editing) });

  useEffect(() => {
    setSelection((current) => ({
      rowIndex: Math.min(current.rowIndex, Math.max(visibleRows.length - 1, 0)),
      columnIndex: Math.min(current.columnIndex, Math.max(columns.length - 1, 0)),
    }));
  }, [columns.length, visibleRows.length]);

  useEffect(() => {
    onSelectedRowChange?.(visibleRows[selection.rowIndex] ?? null);
  }, [onSelectedRowChange, selection.rowIndex, visibleRows]);

  useEffect(() => {
    if (shouldUseVirtualizedGrid) {
      virtualGridRef.current?.scrollToIndex({ index: selection.rowIndex, align: "center", behavior: "auto" });
      return;
    }

    selectedCellRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selection, shouldUseVirtualizedGrid]);

  useEffect(() => {
    if (editingDisabled) {
      setEditing(null);
    }
  }, [editingDisabled]);

  const startEditing = useCallback(
    (nextSelection = selection) => {
      const row = visibleRows[nextSelection.rowIndex];
      const column = columns[nextSelection.columnIndex];
      // Saving cells remain visible but do not reopen for editing.
      // UWHC uses this to keep each pending cell stable while other cells remain available for editing.
      if (editingDisabled || !row || !column || column.editable === false || isSavingCell?.(row, column)) {
        log("ExcelLikeGrid - startEditing - blocked", { rowIndex: nextSelection.rowIndex, column: column?.key });
        return;
      }

      const value = toCellText(row[column.key]);
      log("ExcelLikeGrid - startEditing", { rowIndex: nextSelection.rowIndex, column: column.key });
      setEditing({ ...nextSelection, value, originalValue: value });
    },
    [columns, editingDisabled, isSavingCell, selection, visibleRows],
  );

  const handleFocusGrid = useCallback(() => {
    if (shouldUseVirtualizedGrid) {
      virtualScrollerRef.current?.focus({ preventScroll: true });
      logV("ExcelLikeGrid - handleFocusGrid", { target: "virtual", active: getActiveElementLabel() });
      return;
    }

    gridRef.current?.focus({ preventScroll: true });
    logV("ExcelLikeGrid - handleFocusGrid", { target: "grid", active: getActiveElementLabel() });
  }, [shouldUseVirtualizedGrid]);

  const commitEditingValue = useCallback(
    (nextValue: string, shouldFocusGrid = false) => {
      if (!editing) {
        log("ExcelLikeGrid - commitEditing - no edit");
        return;
      }

      const row = visibleRows[editing.rowIndex];
      const column = columns[editing.columnIndex];
      const changed = nextValue !== editing.originalValue;

      setEditing(null);
      log("ExcelLikeGrid - commitEditing", { shouldFocusGrid, changed });

      if (shouldFocusGrid) {
        logV("ExcelLikeGrid - commitEditing - focus requested");
        window.requestAnimationFrame(handleFocusGrid);
      }

      if (editingDisabled || !row || !column || !changed) {
        return;
      }

      void Promise.resolve(onCellCommit({ row, column, value: nextValue }));
    },
    [columns, editing, editingDisabled, handleFocusGrid, onCellCommit, visibleRows],
  );

  const commitEditing = useCallback(
    (shouldFocusGrid = false) => {
      if (!editing) {
        log("ExcelLikeGrid - commitEditing - no edit");
        return;
      }

      commitEditingValue(editing.value, shouldFocusGrid);
    },
    [commitEditingValue, editing],
  );

  const cancelEditing = useCallback(() => setEditing(null), []);

  const handleGridKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (editing || visibleRows.length === 0 || columns.length === 0) {
        logV("ExcelLikeGrid - onKeyDown - ignored", { key: event.key, editing: Boolean(editing) });
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        log("ExcelLikeGrid - onKeyDown - enter", { rowIndex: selection.rowIndex, columnIndex: selection.columnIndex });
        startEditing();
        return;
      }

      if (event.key === "Tab" && !useTabForCellNavigation) {
        log("ExcelLikeGrid - onKeyDown - tab passed through");
        return;
      }

      const movement = getKeyboardMovement(event.key, event.shiftKey);
      if (!movement) {
        return;
      }

      event.preventDefault();
      log("ExcelLikeGrid - onKeyDown - movement", { key: event.key, row: movement.row, column: movement.column });
      setSelection((current) =>
        moveSelection(
          current.rowIndex,
          current.columnIndex,
          movement.row,
          movement.column,
          visibleRows.length,
          columns.length,
        ),
      );
    },
    [
      columns.length,
      editing,
      selection.columnIndex,
      selection.rowIndex,
      startEditing,
      useTabForCellNavigation,
      visibleRows.length,
    ],
  );

  const handleSortToggle = useCallback((column: ExcelGridColumn) => {
    setSortState((current) => {
      if (current?.columnKey !== column.key) {
        return { columnKey: column.key, direction: "asc" };
      }

      if (current.direction === "asc") {
        return { columnKey: column.key, direction: "desc" };
      }

      return null;
    });
  }, []);

  const updateFilters = useCallback(
    (nextFilters: ExcelGridFilterState) => {
      if (filterState === undefined) setInternalFilters(nextFilters);
      onFilterStateChange?.(nextFilters);
    },
    [filterState, onFilterStateChange],
  );
  const handleClearFilters = useCallback(() => updateFilters({}), [updateFilters]);
  const handleFilterChange = useCallback(
    (columnKey: string, value: string) => {
      log("ExcelLikeGrid - handleFilterChange", { column: columnKey, hasValue: Boolean(value) });
      updateFilters({ ...filters, [columnKey]: value });
      setEditing(null);
      setSelection((current) => ({ ...current, rowIndex: 0 }));
    },
    [filters, updateFilters],
  );
  const scrollerClassName = twMerge(GRID_SCROLLER_CLASS_NAME, GRID_SCROLLBAR_CLASS_NAME, className);
  const virtualizedGridWrapperClassName = getVirtualizedGridHeightClassName(virtualizedMaxHeight);
  const virtualGridComponents = useMemo<TableComponents<ExcelGridRow>>(
    () => ({
      Table: ({ children }) => (
        <table className={GRID_TABLE_CLASS_NAME} width={tableWidth}>
          <ExcelGridColGroup columns={columns} columnWidths={columnWidths} />
          {children}
        </table>
      ),
      TableHead: ExcelGridVirtualTableHead,
      TableRow: ({ item, ...props }) => {
        const tableRowProps = omitInlineStyleProp(props);

        return (
          <tr {...tableRowProps} data-test={`${dataTest}-row-${item.gridRowId}`} className="excel-grid-row align-top" />
        );
      },
    }),
    [columns, columnWidths, dataTest, tableWidth],
  );
  if (rows.length === 0 && !showHeadersWhenEmpty) {
    return (
      <div
        data-test={`${dataTest}-empty`}
        className="border-greyscale-20 flex min-h-[320px] min-w-0 max-w-full items-center justify-center border bg-white text-sm dark:border-gray-700 dark:bg-gray-800"
      >
        {emptyLabel}
      </div>
    );
  }

  if (shouldUseVirtualizedGrid) {
    return (
      <div className={virtualizedGridWrapperClassName}>
        <TableVirtuoso
          ref={virtualGridRef}
          data={visibleRows}
          fixedItemHeight={rowHeight}
          defaultItemHeight={rowHeight ?? DEFAULT_VIRTUALIZED_ROW_HEIGHT}
          overscan={240}
          computeItemKey={(_, row) => row.gridRowId}
          components={virtualGridComponents}
          fixedHeaderContent={() => (
            <ExcelGridHeaderRow
              columns={columns}
              dataTest={dataTest}
              filters={filters}
              sortState={sortState}
              onClearFilters={handleClearFilters}
              onFilterChange={handleFilterChange}
              onSortToggle={handleSortToggle}
              hasFilters={hasFilters}
            />
          )}
          itemContent={(rowIndex, row) => (
            <ExcelGridRowCells
              row={row}
              rowIndex={rowIndex}
              columns={columns}
              rowHeight={rowHeight}
              dataTest={dataTest}
              editing={editing}
              selection={selection}
              selectedCellRef={selectedCellRef}
              onCancelEditing={cancelEditing}
              onCommitEditing={commitEditing}
              onCommitEditingValue={commitEditingValue}
              onEditValueChange={(value) => setEditing((current) => (current ? { ...current, value } : current))}
              onFocusGrid={handleFocusGrid}
              onStartEditing={startEditing}
              onSelectionChange={setSelection}
              isSavingCell={isSavingCell}
              cellEditorMode={cellEditorMode}
              selectMenuPortalTarget={selectMenuPortalTarget}
            />
          )}
          scrollerRef={(ref) => {
            virtualScrollerRef.current = ref instanceof HTMLElement ? ref : null;
          }}
          role="region"
          aria-label={GRID_REGION_LABEL}
          tabIndex={0}
          data-test={dataTest}
          onKeyDown={handleGridKeyDown}
          className={scrollerClassName}
        />
      </div>
    );
  }

  return (
    <div
      ref={gridRef}
      role="region"
      aria-label={GRID_REGION_LABEL}
      tabIndex={0}
      data-test={dataTest}
      onKeyDown={handleGridKeyDown}
      className={scrollerClassName}
    >
      <table className={GRID_TABLE_CLASS_NAME} width={tableWidth}>
        <ExcelGridColGroup columns={columns} columnWidths={columnWidths} />
        <ExcelGridHeader
          columns={columns}
          dataTest={dataTest}
          filters={filters}
          sortState={sortState}
          onClearFilters={handleClearFilters}
          onFilterChange={handleFilterChange}
          onSortToggle={handleSortToggle}
          hasFilters={hasFilters}
        />
        {visibleRows.length > 0 ? (
          <ExcelGridBody
            rows={visibleRows}
            columns={columns}
            rowHeight={rowHeight}
            dataTest={dataTest}
            editing={editing}
            selection={selection}
            selectedCellRef={selectedCellRef}
            onCancelEditing={cancelEditing}
            onCommitEditing={commitEditing}
            onCommitEditingValue={commitEditingValue}
            onEditValueChange={(value) => setEditing((current) => (current ? { ...current, value } : current))}
            onFocusGrid={handleFocusGrid}
            onStartEditing={startEditing}
            onSelectionChange={setSelection}
            isSavingCell={isSavingCell}
            cellEditorMode={cellEditorMode}
            selectMenuPortalTarget={selectMenuPortalTarget}
          />
        ) : rows.length === 0 && showHeadersWhenEmpty ? (
          <tbody data-test={`${dataTest}-empty-body`} />
        ) : (
          <ExcelGridFilteredEmptyBody columnCount={columns.length} dataTest={dataTest} />
        )}
      </table>
    </div>
  );
};
